import React, { useState } from 'react';
import {
  XMarkIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Analytics } from '../../utils/analytics';

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

export default function LeadCaptureModal({ isOpen, onClose, bookTitle }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          book_title: bookTitle || 'Untitled Book',
          source: 'preview_page',
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setStatus('success');
        Analytics.trackLeadSubmitted(email);
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setEmail('');
    setStatus('idle');
    setErrorMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-book max-w-md w-full overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-sepia hover:text-ink transition-colors rounded-lg hover:bg-parchment/50"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {status === 'success' ? (
          // Success state
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-forest" />
            </div>
            <h3 className="font-display text-2xl font-bold text-ink mb-2">
              You're on the list!
            </h3>
            <p className="text-sepia mb-6">
              We'll notify you as soon as printed books are available. 
              Get ready to hold your memories in your hands!
            </p>
            <button
              onClick={handleClose}
              className="btn-primary"
            >
              Continue Exploring
            </button>
          </div>
        ) : (
          // Form state
          <div className="p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gold/20 rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-gold" />
              </div>
              <h3 className="font-display text-2xl font-bold text-ink">
                Get Your Printed Book
              </h3>
            </div>
            
            <p className="text-sepia mb-6">
              Love your preview? We're launching printed books soon! 
              Leave your email and be the first to know when you can order.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                  Email address
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sepia/50" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-field pl-10"
                    disabled={status === 'submitting'}
                    autoFocus
                  />
                </div>
              </div>

              {/* Error message */}
              {status === 'error' && errorMessage && (
                <div className="mb-4 p-3 bg-rust/10 border border-rust/20 rounded-lg flex items-center gap-2 text-rust text-sm">
                  <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                  {errorMessage}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Notify Me
                  </>
                )}
              </button>
            </form>

            {/* Privacy note */}
            <p className="mt-4 text-xs text-sepia/60 text-center">
              We'll only email you about printed book availability. No spam, ever.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


